import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { Button, ImprList, Modal } from '@imprivata-cloud/components';
import { Alert, Button as ImprButton } from '@imprivata-cloud/design-system';

import { ConsentGrantType, Entitlement } from '@api/constants';
import { revokeConsent } from '@api/selfServiceServices';
import { AssuranceType, DocumentPurpose, FactorType } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import {
  useCredentialDelete,
  useUnenrollAuthenticator,
} from '@portal/containers/users/hooks/useAuthenticators';
import type { ColumnType } from 'antd/es/table';
import Icon from '@ant-design/icons';
import getRelativeTime from '../../common/authMethodInfo/relativeTime';
import AuthenticatorActionItems from '../../components/authenticatorActionItems/AuthenticatorActionItems';
import AuthenticatorNameAndIcon from '../../components/authenticatorNameAndIcon/AuthenticatorNameAndIcon';
import { SelfServicePath } from '../SelfServiceRoutes';
import { useSelfServiceData } from '../utils/useSelfServiceData';
import AuthMethodListHeader, {
  type AuthenticatorUiData,
} from './AuthMetodListHeader/AuthMethodListHeader';
import { hasEntitlement } from '@api/utils';
import { sleep } from '@utils/utils';
import { RootRoutes } from '@/index';
import { featureSupported } from '@/featureFlagsUtils';
import { isFaceEnabled } from '../utils/helpers';
import { useHasCamera } from '../utils/useHasCamera';
import { useMfaEnrollmentStatus } from '@portal/hooks/useMfaEnrollmentStatus';
import { usePasswordExpirationInfo } from '@/portal/hooks/usePasswordExpirationInfo';
import { getDaysUntilExpiration } from '@common/utils/passwordExpiration';
import { SessionStorageKeys } from '@common/utils/sessionStorage';
import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import { canEnrollOtp } from '../utils/otpUtils';

import FullPageError from '../../assets/full_page_error.svg?react';
import EmptyImage from '../../assets/self-service-authenticator-list-empty-table.svg?react';
import FaceIcon from '../../assets/icons/authenticators/face_bio.svg?react';
import imprivataIdIcon from '../../assets/icons/authenticators/imprivata_ID.svg?react';
import PasswordIcon from '../../assets/icons/authenticators/password.svg?react';
import OtpIcon from '../../assets/icons/authenticators/otp.svg?react';
import QuestionImg from '../../assets/icons/question.svg?react';
import TimeImg from '../../assets/icons/time.svg?react';

import './AuthenticatorList.less';

const columns: ColumnType[] = [
  {
    dataIndex: 'name-320',
    key: 'name-320',
    responsive: ['xs'],
    render: (_, record) => (
      <>
        <AuthenticatorNameAndIcon factorType={record.factorType} />
        <span style={{ color: 'var(--grey-60)', paddingTop: '9px' }}>{record.additionalText}</span>
      </>
    ),
  },
  {
    dataIndex: 'name',
    key: 'name',
    responsive: ['sm'],
    render: (_, record) => <AuthenticatorNameAndIcon factorType={record.factorType} />,
  },
  {
    dataIndex: 'shortName',
    key: 'shortName',
    responsive: ['sm'],
    render: (_, record) =>
      record.factorType === FactorType.IMPRIVATA_ID && (
        <div>
          {record.shortName}
          {record.deviceModel && ` (${record.deviceModel})`}
        </div>
      ),
  },
  {
    dataIndex: 'additionalText',
    key: 'additionalText',
    responsive: ['sm'],
    render: (_, record) =>
      record.factorType !== FactorType.PASSWORD && (
        <span style={{ color: 'var(--grey-60)' }}>{record.additionalText}</span>
      ),
  },
  {
    dataIndex: 'expiryInfo',
    key: 'expiryInfo',
    responsive: ['sm'],
    render: (_, record) =>
      record.expiryInfo && (
        <span className={record.expiryInfo.isExpired ? 'password-expired' : 'password-warning'}>
          <TimeImg />
          {record.expiryInfo.isExpired
            ? 'Expired'
            : `Expires in ${record.expiryInfo.daysLeft} days`}
        </span>
      ),
  },
  {
    dataIndex: 'action',
    key: 'action',
    render: (_, record) => (
      <AuthenticatorActionItems
        key={record.key}
        factorType={record.factorType}
        onRemove={record.onRemove}
        onRevokeConsent={record?.onRevokeConsent}
        onEdit={record?.onEdit}
        epcs={record?.epcs}
        onEpcsSetup={record?.onEpcsSetup}
      />
    ),
  },
];

interface AuthenticatorToRemove {
  factorType: FactorType;
  remove: () => void;
}

const supportedFactors = new Set([
  FactorType.FACE,
  FactorType.PROX,
  FactorType.IMPRIVATA_ID,
  FactorType.PASSWORD,
  FactorType.OTP,
]);

const TRANSLATION_ROOT = 'self-service.authenticator-list';
const TRANSLATION_ROOT_PASS = 'portal.landing.password-expiration-banner';

const ENABLE_EPCS = featureSupported('ENABLE_EPCS');
const ENABLE_EMAIL_OTP = featureSupported('ENABLE_EMAIL_OTP');

const AuthenticatorList = () => {
  const { t, i18n } = useTranslation();
  const { emitError } = useErrorContext();
  const navigateKeepSearch = useNavigateKeepSearch();

  const { tenantId, userInfo, tenantInfo } = useSession();
  const onError = (e: ApiError) => {
    emitError(new AppError(e));
  };
  const { mutate: unenrollAuthenticator, isPending: isLoadingUnenrollAuthenticator } =
    useUnenrollAuthenticator(tenantId ?? '', { onError });
  const { mutate: deleteCredential, isPending: isPendingDeleteCredential } = useCredentialDelete({
    onError,
  });
  const { hasCamera, loadingCamera } = useHasCamera();
  const [authenticatorToRemove, setAuthenticatorToRemove] = useState<AuthenticatorToRemove>();
  const [consentRevokeDialog, setConsentRevokeDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // MFA enrollment status — gates wrapper rendering so the adjacent-sibling gap
  // rule only fires when an MFA banner will actually appear in the DOM.
  // dismissed tracks the session-scoped dismissal so the wrapper is removed
  // immediately when the user closes the banner (not just on next re-render).
  const mfaStatus = useMfaEnrollmentStatus();
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(
    () => sessionStorage.getItem(SessionStorageKeys.MFA_ENROLLMENT_BANNER_DISMISSED) === 'true',
  );

  // Password expiration info (banner and row)
  const { data: passwordInfo } = usePasswordExpirationInfo();
  const passwordExpiresAt = passwordInfo?.passwordExpiresAt;
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState<number>();
  const daysLeft = getDaysUntilExpiration(passwordExpiresAt);
  const isPasswordExpired = daysLeft === -1;
  const shouldShowPasswordExpiryRow = daysLeft !== null && (isPasswordExpired || daysLeft <= 14);

  const enableEpcs = ENABLE_EPCS && hasEntitlement(tenantInfo, Entitlement.EPCS);

  const {
    data: selfServiceLandingPageData,
    isError: isErrorSelfServiceLandingPageData,
    isLoading: isLoadingSelfServiceLandingPageData,
    refetch: refetchSelfServiceLandingPageData,
  } = useSelfServiceData();

  const authenticatorData: Record<string, AuthenticatorUiData> = useMemo(
    () => ({
      face: {
        text: t('self-service.auth-method.face'),
        icon: <Icon component={FaceIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_ENROLL_FACE.slice(1)),
      },
      password: {
        text: t('self-service.auth-method.password.authn-password'),
        icon: <Icon component={PasswordIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD.slice(1)),
      },
      imprivataId: {
        text: t('self-service.auth-method.imprivata-id.factor'),
        icon: <Icon component={imprivataIdIcon} />,
        onClick: () =>
          navigateKeepSearch(SelfServicePath.SELF_SERVICE_ENROLL_IMPRIVATA_ID.slice(1)),
      },
      otp: {
        text: t('self-service.auth-method.otp.otp-option'),
        icon: <Icon component={OtpIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_OTP.slice(1)),
      },
    }),
    [navigateKeepSearch, t],
  );

  // consentType was removed on API V2. It is guaranteed to return only the impr.biometrics consent type here.
  const consentInfo = selfServiceLandingPageData?.consentInfos?.[0];

  const onSuccess = async () => {
    setAuthenticatorToRemove(undefined);
    await sleep(500);
    await refetchSelfServiceLandingPageData();
  };

  const authnDataSource = selfServiceLandingPageData?.authenticators
    ?.filter(a => supportedFactors.has(a.factorType))
    .map(auth => ({
      key: auth.authenticatorId,
      factorType: auth.factorType,
      additionalText: `${t('common.last-used')} ${getRelativeTime(auth.lastUsedAt, i18n.language)}`,
      shortName: auth.shortName,
      deviceModel: auth.deviceModel,
      onRemove: () =>
        setAuthenticatorToRemove({
          factorType: auth.factorType,
          remove: () => {
            unenrollAuthenticator(auth.authenticatorId, { onSuccess });
          },
        }),
      onRevokeConsent:
        consentInfo?.grantType === ConsentGrantType.GRANTED
          ? () => setConsentRevokeDialog(true)
          : undefined,
      onEdit:
        auth.factorType === FactorType.PASSWORD
          ? () =>
              navigateKeepSearch(
                `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
              )
          : undefined,
      epcs:
        enableEpcs &&
        auth.factorType === FactorType.IMPRIVATA_ID &&
        auth.assuranceType === AssuranceType.IAL2_ASSURED,
      onEpcsSetup:
        enableEpcs &&
        auth.factorType === FactorType.IMPRIVATA_ID &&
        auth.assuranceType === AssuranceType.UNSPECIFIED
          ? () => {
              return navigateKeepSearch(
                `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_EPCS}`,
                {
                  state: {
                    authenticatorId: auth.authenticatorId,
                    authenticatorShortName: auth.shortName,
                    authenticatorDeviceModel: auth.deviceModel,
                  },
                },
              );
            }
          : undefined,
      expiryInfo:
        auth.factorType === FactorType.PASSWORD && shouldShowPasswordExpiryRow
          ? { isExpired: isPasswordExpired, daysLeft: daysLeft ?? 0 }
          : undefined,
    }));

  const credsDataSource = selfServiceLandingPageData?.credentials?.map(cred => ({
    key: cred.credentialId,
    factorType: FactorType.LEARNED_CREDS,
    additionalText: cred.appDisplayName,
    onRemove: () =>
      setAuthenticatorToRemove({
        factorType: FactorType.LEARNED_CREDS,
        remove: () => {
          deleteCredential(cred.credentialId, { onSuccess });
        },
      }),
  }));

  const availableAuthMethods: Record<string, AuthenticatorUiData> = useMemo(() => {
    if (isLoadingSelfServiceLandingPageData || !selfServiceLandingPageData || loadingCamera) {
      return {};
    }

    const newMethods: Record<string, AuthenticatorUiData> = {};

    const IMPRIVATA_DOMAIN = 'imprivata.id';
    const hasImprivataId =
      userInfo?.email?.includes(IMPRIVATA_DOMAIN) ||
      userInfo?.userDisplayName?.includes(IMPRIVATA_DOMAIN);

    if (hasImprivataId) {
      newMethods.password = authenticatorData.password;
    }

    newMethods.imprivataId = authenticatorData.imprivataId;

    if (isFaceEnabled(selfServiceLandingPageData) && hasCamera) {
      newMethods.face = authenticatorData.face;
    }

    if (
      canEnrollOtp({
        enableEmailOtp: ENABLE_EMAIL_OTP,
        email: userInfo?.email,
        userDisplayName: userInfo?.userDisplayName,
        authenticators: selfServiceLandingPageData?.authenticators,
      })
    ) {
      newMethods.otp = authenticatorData.otp;
    }

    return newMethods;
  }, [
    isLoadingSelfServiceLandingPageData,
    selfServiceLandingPageData,
    hasCamera,
    userInfo?.email,
    userInfo?.userDisplayName,
    authenticatorData,
    loadingCamera,
  ]);

  const onRevokeConsent = async () => {
    setDialogLoading(true);
    try {
      await revokeConsent({
        consentRevokeList:
          consentInfo?.documentMetadataList.filter(
            d => d.documentPurpose !== DocumentPurpose.NOTICE,
          ) || [],
      });
      await sleep(500);
      setConsentRevokeDialog(false);
      await refetchSelfServiceLandingPageData();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setDialogLoading(false);
    }
  };

  // Show (or not) password expiration banner
  useEffect(() => {
    const dismissed = sessionStorage.getItem(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_MII);
    if (daysLeft !== null) {
      setDaysUntilExpiration(daysLeft);
      const shouldShow = daysLeft <= 14 && daysLeft > 0 && !dismissed;
      setShowPasswordAlert(shouldShow);
    }
  }, [daysLeft]);

  if (isErrorSelfServiceLandingPageData) {
    return (
      <div className="flex flex-col justify-center p-4 align-center">
        <div className="mb-4 flex justify-center">
          <FullPageError />
        </div>
        <div className="mb-2 p-4">
          Unable to load authenticators. Try again after a few minutes.
        </div>
        <Button
          type="primary"
          onClick={() => refetchSelfServiceLandingPageData()}
          className="ant-btn ant-btn-primary"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const passwordAlertActions = (
    <ImprButton
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        navigateKeepSearch(
          `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
          { state: { from: globalThis.location.pathname + globalThis.location.search } },
        );
      }}
    >
      {t(`${TRANSLATION_ROOT_PASS}.update-button`)}
    </ImprButton>
  );

  return (
    <div className="authenticator-list-container">
      {showPasswordAlert && (
        <div className="password-expiration-alert">
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT_PASS}.title`, { count: daysUntilExpiration })}
            message={t(`${TRANSLATION_ROOT_PASS}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setShowPasswordAlert(false);
              sessionStorage.setItem(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_MII, 'true');
            }}
          />
        </div>
      )}
      {mfaStatus && (!mfaBannerDismissed || mfaStatus.isPastDeadline) && (
        <div className="mfa-enrollment-alert-wrapper">
          <MfaEnrollmentBanner hideCta onDismiss={() => setMfaBannerDismissed(true)} />
        </div>
      )}
      <Modal
        open={!!authenticatorToRemove}
        onCancel={() => setAuthenticatorToRemove(undefined)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{
          type: 'default',
          loading: isLoadingUnenrollAuthenticator || isPendingDeleteCredential,
        }}
        okText={t('self-service.authenticator-list.confirm-remove-face-btn')}
        cancelText={t('self-service.authenticator-list.cancel-remove-face')}
        onOk={authenticatorToRemove?.remove}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
        maskClosable={false}
      >
        <QuestionImg className="modal-graphic" />
        <h2>
          {t(`${TRANSLATION_ROOT}.remove-authenticator-title`, {
            authenticator: t(
              `${TRANSLATION_ROOT}.authenticators.${authenticatorToRemove?.factorType}`,
            ),
          })}
        </h2>
        <p>
          {t(`${TRANSLATION_ROOT}.confirm-remove-authenticator`, {
            authenticator: t(
              `${TRANSLATION_ROOT}.authenticators.${authenticatorToRemove?.factorType}`,
            ),
          })}
        </p>
      </Modal>
      <Modal
        open={consentRevokeDialog}
        onCancel={() => setConsentRevokeDialog(false)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{ type: 'default', loading: dialogLoading }}
        okText={t('self-service.authenticator-list.confirm-revoke-consent')}
        cancelText={t('self-service.authenticator-list.cancel-revoke-consent')}
        onOk={onRevokeConsent}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
        maskClosable={false}
      >
        <QuestionImg className="modal-graphic" />
        <h2>{t('self-service.authenticator-list.revoke-consent-title')}</h2>
        <p>{t('self-service.authenticator-list.revoke-consent-content')}</p>
      </Modal>
      <ImprList
        loading={isLoadingSelfServiceLandingPageData}
        columns={columns}
        dataSource={
          [...(authnDataSource ?? []), ...(credsDataSource ?? [])] as Record<string, unknown>[]
        }
        showHeader={false}
        pagination={false}
        enableSearch={false}
        headerExtraComponents={[
          <AuthMethodListHeader
            authenticatorsList={availableAuthMethods}
            key="table-extra-header"
          />,
        ]}
        locale={{
          emptyText: (
            <div className={'empty-list-container'}>
              <EmptyImage style={{ margin: '1rem' }} />
              <div className={'empty-list-container-title'}>
                {t('self-service.authenticator-list.empty-text')}
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default AuthenticatorList;
